import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Mic, Heart, ArrowRight, Send, CheckCircle, ShieldCheck, Globe, Mountain } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { PageTransition } from '../components/PageTransition';
export function HomePage() {
  const [formResult, setFormResult] = useState('');
  const [captchaChecked, setCaptchaChecked] = useState(false);
  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!captchaChecked) {
      setFormResult('Please verify you are not a robot.');
      return;
    }
    setFormResult('Sending....');
    const formData = new FormData(event.currentTarget);
    formData.append('access_key', '7ab87c60-088d-4fc0-91c1-42d023f2a608');
    const response = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      body: formData
    });
    const data = await response.json();
    if (data.success) {
      setFormResult('Form Submitted Successfully');
      event.currentTarget.reset();
    } else {
      setFormResult('Error');
    }
  };
  return <PageTransition data-id="element-307">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-start justify-center overflow-hidden pt-28" data-id="element-308">
        {/* Background Image */}
        <div className="absolute inset-0 z-0" data-id="element-309">
          <img src="https://cdn.magicpatterns.com/uploads/4XsQyzBu4UT6zjnQT4ucNY/Person_praying_and_kneeling_with_arms_raised.webp" alt="" className="w-full h-full object-cover" data-id="element-310" />
          <div className="absolute inset-0 bg-dark-900/65" data-id="element-311"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center" data-id="element-312">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} className="mb-6 mt-8 flex justify-center" data-id="element-313">
            <img src="https://cdn.magicpatterns.com/uploads/ue8vBFZwEq6iFGtuLhrkM6/Podcastlogo3doves.jpg" alt="3 Doves Podcast Logo" className="w-32 h-32 md:w-48 md:h-48 rounded-full border-4 border-gold-500/20 shadow-2xl shadow-gold-500/10" data-id="element-314" />
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.8
        }} className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-6 leading-tight" data-id="element-315">
            Honest Faith. <span className="text-gold-500" data-id="element-316">Real Stories.</span>
            <br data-id="element-317" />
            Lives Transformed.
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5,
          duration: 0.8
        }} className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-318">
            Join Jason Pawloski for weekly conversations about walking with God
            in the middle of everyday life. No polish, just truth.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7,
          duration: 0.8
        }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-319">
            <a href="#listen-platforms" onClick={e => {
            e.preventDefault();
            document.getElementById('listen-platforms')?.scrollIntoView({
              behavior: 'smooth'
            });
          }} data-id="element-320">
              <Button size="lg" leftIcon={<Play className="w-5 h-5 fill-current" data-id="element-322" />} data-id="element-321">
                Listen Now
              </Button>
            </a>
            <Link to="/guests" data-id="element-323">
              <Button variant="outline" size="lg" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-325" />} data-id="element-324">
                Meet Our Guests
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Featured Episode */}
      <section className="py-20 bg-dark-800 relative border-t border-dark-600" data-id="element-326">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-327">
          <div className="flex flex-col md:flex-row items-center gap-12" data-id="element-328">
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="w-full md:w-1/2" data-id="element-329">
              <div className="relative aspect-square rounded-2xl overflow-hidden bg-dark-600 border border-dark-500 group" data-id="element-330">
                <img src="https://cdn.magicpatterns.com/uploads/izDxhmZEA5fkGRptKrG4Ua/D5EDBD74-F376-4789-B44A-5261BC84DE34.jpg" alt="Music, Mission & Ministry — Episode 11 with Iskander" className="w-full h-full object-cover" data-id="element-331" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/10 transition-all duration-300" data-id="element-332">
                  <div className="w-16 h-16 rounded-full bg-gold-500 flex items-center justify-center text-dark-900 shadow-lg shadow-gold-500/30 group-hover:scale-110 transition-transform duration-300" data-id="element-333">
                    <Play className="w-7 h-7 fill-current ml-1" data-id="element-334" />
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="w-full md:w-1/2" data-id="element-335">
              <span className="text-gold-500 font-medium tracking-wider uppercase text-sm mb-2 block" data-id="element-336">
                Latest Release
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6" data-id="element-337">
                Music, Mission & Ministry — A Conversation with Iskander
              </h2>
              <p className="text-white/80 text-lg mb-8 leading-relaxed" data-id="element-338">
                Born Jesús Martínez in Venezuela, Iskander is a dynamic reggae
                musician and passionate missionary whose work spans music,
                ministry, and humanitarian outreach worldwide. Through his
                powerful sound, he conveys messages of love, hope, and
                faith—reaching audiences across cultures while serving
                marginalized communities, from the homeless to at-risk youth.
              </p>
              <div className="flex items-center gap-4 text-sm text-white/60 mb-8" data-id="element-339">
                <span className="flex items-center" data-id="element-340">
                  <Mic className="w-4 h-4 mr-2 text-gold-500" data-id="element-341" /> Episode 11
                </span>
                <span className="w-1 h-1 rounded-full bg-dark-400" data-id="element-342"></span>
                <span data-id="element-343">with Iskander</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-344">
                <Link to="/podcasts" data-id="element-345">
                  <Button leftIcon={<Play className="w-4 h-4 fill-current" data-id="element-347" />} data-id="element-346">
                    Listen Now
                  </Button>
                </Link>
                <Link to="/podcasts" data-id="element-348">
                  <Button variant="outline" data-id="element-349">View All Episodes</Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Next Episode - Coming Soon */}
      <section className="py-20 bg-dark-700 relative border-t border-dark-600" data-id="element-350">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-351">
          <div className="flex flex-col md:flex-row-reverse items-center gap-12" data-id="element-352">
            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="w-full md:w-1/2" data-id="element-353">
              <div className="relative aspect-square rounded-2xl overflow-hidden bg-dark-600 border border-dark-500" data-id="element-354">
                <img src="https://cdn.magicpatterns.com/uploads/vW2YK8ZWktRa4PbazYs6Rb/IMG_7779.jpg" alt="Cody Grube — Sharing the Light" className="w-full h-full object-cover" data-id="element-355" />
                <div className="absolute top-6 left-6 bg-gold-500 text-dark-900 px-4 py-2 rounded-full font-bold text-sm shadow-lg" data-id="element-356">
                  Coming Soon
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="w-full md:w-1/2" data-id="element-357">
              <span className="text-gold-500 font-medium tracking-wider uppercase text-sm mb-2 block" data-id="element-358">
                Coming Up Next
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6" data-id="element-359">
                Sharing the Light — A Conversation with Cody Grube
              </h2>
              <p className="text-white/80 text-lg mb-8 leading-relaxed" data-id="element-360">
                Cody Grube is the founder of Streetlights Ministries, a powerful
                outreach dedicated to sharing the light of Christ on the streets
                with those who need it most. Inspired by Isaiah 35:5-9 and
                grounded in the call to love God and love your neighbor, Cody
                brings hope, healing, and the transformative message of the
                Gospel to the streets and beaches.
              </p>
              <div className="flex items-center gap-4 text-sm text-white/60 mb-8" data-id="element-361">
                <span className="flex items-center" data-id="element-362">
                  <Mic className="w-4 h-4 mr-2 text-gold-500" data-id="element-363" /> Episode 12
                </span>
                <span className="w-1 h-1 rounded-full bg-dark-400" data-id="element-364"></span>
                <span data-id="element-365">with Cody Grube</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-366">
                <Button variant="outline" disabled data-id="element-367">
                  Coming Soon
                </Button>
                <a href="https://streetlightsministries.org/" target="_blank" rel="noopener noreferrer" data-id="element-368">
                  <Button variant="ghost" data-id="element-369">Visit Streetlights Ministries</Button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Outreach Programs */}
      <section className="py-24 bg-dark-700" data-id="element-370">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-371">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-372">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-373">
              Outreach Programs We Support
            </h2>
            <p className="text-white/80 text-lg max-w-3xl mx-auto" data-id="element-374">
              Faith without action is incomplete. We partner with organizations
              making a real difference in the lives of those who need it most.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-375">
            {/* Operation Olive Branch */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} data-id="element-376">
              <Card className="h-full bg-dark-800 border-dark-600 p-8" data-id="element-377">
                <div className="mb-6" data-id="element-378">
                  <img src="https://cdn.magicpatterns.com/uploads/mmGRaZje5gHPYZ1hRf9zTx/operationolivebranch.jpg" alt="Operation Olive Branch" className="w-48 h-auto mx-auto" data-id="element-379" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-white mb-3 text-center" data-id="element-380">
                  Operation Olive Branch
                </h3>
                <p className="text-gold-500 text-sm text-center mb-6 uppercase tracking-wider" data-id="element-381">
                  Serving the Least of These
                </p>
                <p className="text-white/80 leading-relaxed mb-8" data-id="element-382">
                  Our Essential Bags program provides homeless individuals with
                  dignity and basic necessities — because every person is made
                  in the image of God. Each bag includes toiletries, hygiene
                  products, water, and snacks to meet immediate needs with
                  compassion.
                </p>
                <Link to="/outreach" data-id="element-383">
                  <Button variant="outline" className="w-full" data-id="element-384">
                    Learn More
                  </Button>
                </Link>
              </Card>
            </motion.div>

            {/* The Million Movement */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} data-id="element-385">
              <Card className="h-full bg-dark-800 border-dark-600 p-8" data-id="element-386">
                <div className="mb-6 flex justify-center" data-id="element-387">
                  <Globe className="w-24 h-24 text-gold-500" data-id="element-388" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-white mb-3 text-center" data-id="element-389">
                  The Million Movement
                </h3>
                <p className="text-gold-500 text-sm text-center mb-6 uppercase tracking-wider" data-id="element-390">
                  Child Sponsorship
                </p>
                <p className="text-white/80 leading-relaxed mb-8" data-id="element-391">
                  Founded by Child Advocate Jeremy Willet, The Million Movement
                  produces award-winning campaigns that travel on nationwide
                  concert tours to find sponsors for children living in poverty.
                  Their goal: one million children sponsored by 2050.
                </p>
                <Link to="/million-movement" data-id="element-392">
                  <Button variant="outline" className="w-full" data-id="element-393">
                    Learn More
                  </Button>
                </Link>
              </Card>
            </motion.div>

            {/* Ends Adventures */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3
          }} data-id="element-394">
              <Card className="h-full bg-dark-800 border-dark-600 p-8" data-id="element-395">
                <div className="mb-6 flex justify-center" data-id="element-396">
                  <Mountain className="w-24 h-24 text-gold-500" data-id="element-397" />
                </div>
                <h3 className="text-2xl font-serif font-bold text-white mb-3 text-center" data-id="element-398">
                  Ends of the Earth Adventures
                </h3>
                <p className="text-gold-500 text-sm text-center mb-6 uppercase tracking-wider" data-id="element-399">
                  Exercise Your Faith
                </p>
                <p className="text-white/80 leading-relaxed mb-8" data-id="element-400">
                  Since 2012, a ministry using outdoor adventure to strengthen
                  faith and build authentic community. Step away from the noise
                  and into creation to hear God's voice, face challenges with
                  resilience, and forge deep relationships. #PrayGoRepeat
                </p>
                <Link to="/ends-adventures" data-id="element-401">
                  <Button variant="outline" className="w-full" data-id="element-402">
                    Learn More
                  </Button>
                </Link>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-dark-800" data-id="element-403">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-404">
          <div className="text-center mb-16" data-id="element-405">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-406">
              What We Believe
            </h2>
            <div className="w-24 h-1 bg-gold-500 mx-auto rounded-full" data-id="element-407"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-408">
            {[{
            title: 'Real Stories',
            desc: "No scripts, no pretense. Just honest conversations about life's actual struggles and victories.",
            icon: <Mic className="w-8 h-8 text-gold-500" data-id="element-409" />
          }, {
            title: 'Living Faith',
            desc: "Faith isn't just for Sunday. We explore how to walk with Jesus in our work, families, and doubts.",
            icon: <Heart className="w-8 h-8 text-gold-500" data-id="element-410" />
          }, {
            title: 'Grace in Process',
            desc: 'We celebrate progress, not perfection. God meets us exactly where we are, messy parts included.',
            icon: <Play className="w-8 h-8 text-gold-500" data-id="element-411" />
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2
          }} data-id="element-412">
                <Card className="p-8 h-full text-center bg-dark-600 border-dark-500" data-id="element-413">
                  <div className="w-16 h-16 mx-auto bg-dark-500 rounded-full flex items-center justify-center mb-6 border border-dark-400" data-id="element-414">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-serif font-bold text-white mb-4" data-id="element-415">
                    {item.title}
                  </h3>
                  <p className="text-white/80 leading-relaxed" data-id="element-416">{item.desc}</p>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Listen On Section */}
      <section id="listen-platforms" className="py-20 bg-dark-800 border-t border-dark-600" data-id="element-417">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-418">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-419">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-420">
              Listen On Your Favorite Platform
            </h2>
            <div className="w-24 h-1 bg-gold-500 mx-auto rounded-full" data-id="element-421"></div>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-422">
            {[{
            name: 'Spotify',
            url: 'https://open.spotify.com/show/5nWVRvG1GtlfUXUfN77LfL',
            color: '#1DB954',
            bg: 'bg-[#1DB954]/10',
            border: 'border-[#1DB954]/30',
            hoverBorder: 'hover:border-[#1DB954]/70'
          }, {
            name: 'Apple Podcasts',
            url: 'https://podcasts.apple.com/us/podcast/3-doves-podcast/id1871795398',
            color: '#B150E2',
            bg: 'bg-[#B150E2]/10',
            border: 'border-[#B150E2]/30',
            hoverBorder: 'hover:border-[#B150E2]/70'
          }, {
            name: 'Amazon Music',
            url: 'https://music.amazon.com/podcasts/1078f776-3803-4274-8e27-29857c6a295e/3-doves-podcast',
            color: '#00A8E1',
            bg: 'bg-[#00A8E1]/10',
            border: 'border-[#00A8E1]/30',
            hoverBorder: 'hover:border-[#00A8E1]/70'
          }, {
            name: 'iHeart Radio',
            url: 'https://www.iheart.com/podcast/269-3-doves-podcast-319099933/',
            color: '#C6002B',
            bg: 'bg-[#C6002B]/10',
            border: 'border-[#C6002B]/30',
            hoverBorder: 'hover:border-[#C6002B]/70'
          }].map((platform, index) => <motion.a key={platform.name} href={platform.url} target="_blank" rel="noopener noreferrer" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className={`flex flex-col items-center justify-center gap-4 p-6 rounded-xl border ${platform.bg} ${platform.border} ${platform.hoverBorder} transition-all duration-300 group`} data-id="element-423">
                <div className="w-14 h-14 flex items-center justify-center group-hover:scale-110 transition-transform duration-300" data-id="element-424">
                  {platform.name === 'Spotify' && <svg viewBox="0 0 24 24" className="w-12 h-12" fill="#1DB954" data-id="element-425">
                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" data-id="element-426" />
                    </svg>}
                  {platform.name === 'Apple Podcasts' && <svg viewBox="0 0 50 50" className="w-12 h-12" fill="none" data-id="element-427">
                      <defs data-id="element-428">
                        <linearGradient id="applePodGrad" x1="25" y1="2" x2="25" y2="48" gradientUnits="userSpaceOnUse" data-id="element-429">
                          <stop stopColor="#F452FF" data-id="element-430" />
                          <stop offset="1" stopColor="#832BC1" data-id="element-431" />
                        </linearGradient>
                      </defs>
                      <rect width="50" height="50" rx="11" fill="url(#applePodGrad)" data-id="element-432" />
                      <path d="M25 11.5a8.5 8.5 0 0 0-3.5 16.23V38a3.5 3.5 0 1 0 7 0V27.73A8.5 8.5 0 0 0 25 11.5Zm0 4a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Z" fill="#fff" data-id="element-433" />
                      <path d="M25 7C16.72 7 10 13.72 10 22c0 4.97 2.42 9.37 6.14 12.1a1.5 1.5 0 1 0 1.72-2.46A11.96 11.96 0 0 1 13 22c0-6.63 5.37-12 12-12s12 5.37 12 12a11.96 11.96 0 0 1-4.86 9.64 1.5 1.5 0 1 0 1.72 2.46C37.58 31.37 40 26.97 40 22c0-8.28-6.72-15-15-15Z" fill="#fff" data-id="element-434" />
                    </svg>}
                  {platform.name === 'Amazon Music' && <svg viewBox="0 0 24 24" className="w-12 h-12" fill="#00A8E1" data-id="element-435">
                      <path d="M14.43 17.59c-2.86.88-6.17 1.35-9.3.95-.52-.07-.61.39-.13.59 2.96 1.2 6.88 1.91 10.21 1.37.74-.12.87-.86.22-1.09-.65-.23-1-.82-1-.82zm1.51-1.14c-.11-.14-.28-.15-.42-.07-1.5.94-3.5 1.54-5.63 1.54-2.37 0-4.51-.68-6.1-1.81-.13-.09-.3-.08-.39.07-.09.15-.05.33.08.42 1.72 1.23 4.02 1.97 6.41 1.97 2.28 0 4.38-.63 5.98-1.7.13-.09.18-.27.07-.42zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5.16 14.5c-2.02 1.5-4.76 2.38-7.66 2.38-3.53 0-6.71-1.31-9.13-3.47.48.48 1.04.89 1.66 1.22 1.72 1.23 4.02 1.97 6.41 1.97 2.28 0 4.38-.63 5.98-1.7 1.6-1.07 2.74-2.61 3.13-4.4.09-.41-.22-.78-.64-.78h-.01c-.31 0-.58.22-.64.52-.31 1.54-1.26 2.87-2.6 3.76z" data-id="element-436" />
                    </svg>}
                  {platform.name === 'iHeart Radio' && <svg viewBox="0 0 24 24" className="w-12 h-12" fill="#C6002B" data-id="element-437">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" data-id="element-438" />
                    </svg>}
                </div>
                <span className="text-sm font-medium text-white/80 group-hover:text-white transition-colors text-center" data-id="element-439">
                  {platform.name}
                </span>
              </motion.a>)}
          </div>
        </div>
      </section>

      {/* Connect Form Section */}
      <section className="py-24 bg-dark-700 border-t border-dark-600" data-id="element-440">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-441">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-442">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-443">
              Connect With Us
            </h2>
            <div className="w-24 h-1 bg-gold-500 mx-auto rounded-full mb-6" data-id="element-444"></div>
            <p className="text-xl text-white/80" data-id="element-445">
              Have a story to share? Want to be a guest? Drop us a message.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-dark-800 rounded-2xl p-6 md:p-10 border border-dark-600 shadow-xl" data-id="element-446">
            <form onSubmit={onSubmit} className="space-y-6" data-id="element-447">
              <div data-id="element-448">
                <label htmlFor="name" className="block text-sm font-medium text-white/70 mb-2" data-id="element-449">
                  Name
                </label>
                <input type="text" name="name" id="name" required placeholder="Your name" className="w-full px-4 py-3 bg-dark-700 border border-dark-500 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors" data-id="element-450" />
              </div>
              <div data-id="element-451">
                <label htmlFor="email" className="block text-sm font-medium text-white/70 mb-2" data-id="element-452">
                  Email
                </label>
                <input type="email" name="email" id="email" required placeholder="your@email.com" className="w-full px-4 py-3 bg-dark-700 border border-dark-500 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors" data-id="element-453" />
              </div>
              <div data-id="element-454">
                <label htmlFor="phone" className="block text-sm font-medium text-white/70 mb-2" data-id="element-455">
                  Phone Number
                </label>
                <input type="tel" name="phone" id="phone" placeholder="(555) 123-4567" className="w-full px-4 py-3 bg-dark-700 border border-dark-500 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors" data-id="element-456" />
              </div>
              <div data-id="element-457">
                <label htmlFor="message" className="block text-sm font-medium text-white/70 mb-2" data-id="element-458">
                  Message
                </label>
                <textarea name="message" id="message" required rows={5} placeholder="Tell us your story, ask a question, or share how we can pray for you..." className="w-full px-4 py-3 bg-dark-700 border border-dark-500 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 transition-colors resize-none" data-id="element-459" />
              </div>
              {/* Captcha Checkbox */}
              <div onClick={() => setCaptchaChecked(!captchaChecked)} className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all duration-200 select-none ${captchaChecked ? 'bg-dark-600 border-gold-500/50' : 'bg-dark-700 border-dark-500 hover:border-dark-400'}`} data-id="element-460">
                <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200 ${captchaChecked ? 'bg-gold-500 border-gold-500' : 'border-dark-400 bg-transparent'}`} data-id="element-461">
                  {captchaChecked && <ShieldCheck className="w-4 h-4 text-dark-900" data-id="element-462" />}
                </div>
                <span className="text-white/80 text-sm" data-id="element-463">I'm not a robot</span>
                <input type="hidden" name="botcheck" value="" data-id="element-464" />
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-465">
                <Button type="submit" size="lg" leftIcon={<Send className="w-4 h-4" data-id="element-467" />} data-id="element-466">
                  Send Message
                </Button>
                {formResult && <span className={`text-sm font-medium flex items-center gap-2 ${formResult === 'Form Submitted Successfully' ? 'text-green-400' : formResult === 'Error' || formResult === 'Please verify you are not a robot.' ? 'text-red-400' : 'text-gold-400'}`} data-id="element-468">
                    {formResult === 'Form Submitted Successfully' && <CheckCircle className="w-4 h-4" data-id="element-469" />}
                    {formResult}
                  </span>}
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden" data-id="element-470">
        <div className="absolute inset-0 bg-gold-600/10" data-id="element-471"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-472">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6" data-id="element-473">
            Support the Mission
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-474">
            Help us continue sharing these stories and providing essential bags
            to those in need through our outreach program.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-475">
            <Link to="/donate" data-id="element-476">
              <Button size="lg" data-id="element-477">Give Now</Button>
            </Link>
            <Link to="/outreach" data-id="element-478">
              <Button variant="outline" size="lg" data-id="element-479">
                Learn About Outreach
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </PageTransition>;
}